import React, { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTonConnectUI } from "@tonconnect/ui-react";

import { useConfigureTelegramWebApp } from "@/modules/shared/hooks/use-configure-telegram-web-app";
import { DisconnectWalletButton } from "@/modules/shared/atoms/disconnect-wallet-button";
import { useUpdateUser } from "@/modules/shared/hooks/use-update-user";
import { useShareAnalyticsEvent } from "@/modules/analytics/hooks/use-share-analytics-event";
import { EAnalyticsEvent } from "@/modules/analytics/consts/analytics-event";
import { LoadingScreen } from "@/modules/shared/atoms/loading-screen";
import { VersionMessage } from "@/modules/shared/atoms/version-message";
import { useEruda } from "@/modules/shared/hooks/use-eruda";
import { ParamsMessage } from "@/modules/shared/atoms/params-message";
import { AddReferrals } from "@/modules/shared/atoms/add-referrals";
import { crumb, logEx, logMsg } from "@/config/sentry-logger";
import { handleStartParam } from "@/modules/shared/utils/handle-start-param";

import type { ExtendedUpdateUserData } from "@/modules/shared/atoms/params-message/types";
import type { RootState } from "@/store";

const App = () => {
  useConfigureTelegramWebApp();
  useEruda();

  const initData = useSelector((state: RootState) => state.telegram.initData);
  const isInitDataObtained = useSelector(
    (state: RootState) => state.telegram.isInitDataObtained,
  );

  const { updateUser } = useUpdateUser();
  const navigate = useNavigate();

  const { shareAnalyticsEvent } = useShareAnalyticsEvent();
  const appWasOpenedSharedRef = useRef(false);

  const [tonConnectUI] = useTonConnectUI();
  const [initialized, setInitialized] = useState(false);
  const [userData, setUserData] = useState<ExtendedUpdateUserData>({});

  useEffect(() => {
    if (tonConnectUI.connected) {
      crumb("TON wallet connected");
    }
  }, [tonConnectUI.connected]);

  useEffect(() => {
    if (isInitDataObtained && !initialized && initData) {
      (async () => {
        try {
          await handleStartParam({
            initData,
            updateUser,
            navigate,
            setUserData,
            setInitialized,
          });
        } catch (error) {
          logMsg("Unhandled error in startParam effect", "error");
          logEx(error, "init useEffect fatal");
          setInitialized(true);
        }
      })();
    }
  }, [isInitDataObtained, initialized, initData, updateUser, navigate]);

  useEffect(() => {
    const send = async () => {
      if (!appWasOpenedSharedRef.current && initialized) {
        try {
          await shareAnalyticsEvent(EAnalyticsEvent.APP_WAS_OPENED);
          appWasOpenedSharedRef.current = true;
          crumb("APP_WAS_OPENED sent");
        } catch (error) {
          logMsg("shareAnalyticsEvent(APP_WAS_OPENED) failed", "warning");
          logEx(error, "APP_WAS_OPENED analytics error");
        }
      }
    };
    void send();
  }, [initialized, shareAnalyticsEvent]);

  return initData && initialized ? (
    <>
      <Outlet />
      <ParamsMessage userData={userData} />
      <VersionMessage />
      <DisconnectWalletButton />
      <AddReferrals />
    </>
  ) : (
    <LoadingScreen />
  );
};

export default App;
